/**
 * Centralized date formatting utilities
 * Standard format: gg/mm/aaaa (e.g., 27/01/2026)
 * 
 * IMPORTANT: These functions only change DISPLAY format.
 * Internal storage, API, and calculations remain in ISO format (YYYY-MM-DD)
 */

/**
 * Format date as gg/mm/aaaa
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date (e.g., "27/01/2026")
 */
export const formatDisplayDate = (dateInput) => {
  if (!dateInput) return "—";
  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return "—";
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch {
    return "—";
  }
};

/**
 * Format date with time as gg/mm/aaaa HH:mm
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date with time (e.g., "27/01/2026 14:30")
 */
export const formatDisplayDateTime = (dateInput) => {
  if (!dateInput) return "—";
  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return "—";
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return "—";
  }
};

/**
 * Format date with weekday as "Lun 27/01/2026"
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Formatted date with weekday
 */
export const formatDisplayDateWithWeekday = (dateInput) => {
  if (!dateInput) return "—";
  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return "—";
    
    const weekdays = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
    const weekday = weekdays[date.getDay()];
    
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    
    return `${weekday} ${day}/${month}/${year}`;
  } catch {
    return "—";
  }
};

/**
 * Format date range as "27/01/2026 - 30/01/2026"
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted date range
 */
export const formatDisplayDateRange = (startDate, endDate) => {
  const start = formatDisplayDate(startDate);
  const end = formatDisplayDate(endDate);
  return `${start} - ${end}`;
};

/**
 * Format month/year as "Gennaio 2026"
 * @param {number} month - Month (1-12)
 * @param {number} year - Year
 * @returns {string} Formatted month and year
 */
export const formatMonthYear = (month, year) => {
  const monthNames = [
    "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
  ];
  return `${monthNames[month - 1] || ""} ${year}`;
};

/**
 * Format relative time (for recent items)
 * @param {string|Date} dateInput - ISO date string or Date object
 * @returns {string} Relative time or formatted date
 */
export const formatRelativeTime = (dateInput) => {
  if (!dateInput) return "—";
  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return "—";
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Adesso";
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    
    return formatDisplayDate(date);
  } catch {
    return "—";
  }
};
